import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  CreatePurchaseOrderRequest,
  PurchaseOrder,
  PurchaseOrderPaymentMethod,
  PurchaseOrderSearchRequest,
  ReceivePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
} from './purchase-order';
import { handleRequest } from '../quipli-service-client';
import { Pagination } from '../pagination/pagination';
import { AxiosRequestConfig } from 'axios';
import { PaginationParsers } from '../pagination/pagination-parsers';
import { PurchaseOrderParsers } from './purchase-order-parsers';

export const usePurchaseOrderApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function createPurchaseOrder(req: CreatePurchaseOrderRequest): Promise<PurchaseOrder> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const json = await handleRequest<PurchaseOrderParsers.PurchaseOrderJson>(config);
    return PurchaseOrderParsers.toPurchaseOrder(json);
  }

  async function updatePurchaseOrder(purchaseOrderId: string, req: UpdatePurchaseOrderRequest): Promise<PurchaseOrder> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/purchase-orders/${purchaseOrderId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const json = await handleRequest<PurchaseOrderParsers.PurchaseOrderJson>(config);
    return PurchaseOrderParsers.toPurchaseOrder(json);
  }

  async function searchPurchaseOrders(req: PurchaseOrderSearchRequest): Promise<Pagination<PurchaseOrder>> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<PurchaseOrderParsers.PurchaseOrderJson>
    >(config);
    return {
      items: items.map(PurchaseOrderParsers.toPurchaseOrder),
      pagination,
    };
  }

  async function getPurchaseOrderById(id: string): Promise<PurchaseOrder> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/purchase-orders/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<PurchaseOrderParsers.PurchaseOrderJson>(config);
    return PurchaseOrderParsers.toPurchaseOrder(result);
  }

  async function approvePurchaseOrder(id: string): Promise<void> {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/v1/purchase-orders/${id}/approve`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    await handleRequest<void>(config);
  }

  async function receivePurchaseOrder(id: string, request: ReceivePurchaseOrderRequest): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders/${id}/receive`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    await handleRequest<void>(config);
  }

  async function cancelPurchaseOrder(id: string): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders/${id}/cancel`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    await handleRequest<void>(config);
  }

  async function listPaymentMethods(): Promise<PurchaseOrderPaymentMethod[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/purchase-orders/payment-methods`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<PurchaseOrderPaymentMethod[]>(config);
  }

  async function createPaymentMethod(name: string, description?: string) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders/payment-methods`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        name,
        description,
      },
    };

    return await handleRequest<PurchaseOrderPaymentMethod>(config);
  }

  async function updatePaymentMethod(id: string, name: string, description?: string) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/purchase-orders/payment-methods/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        name,
        description,
      },
    };

    return await handleRequest<PurchaseOrderPaymentMethod>(config);
  }

  async function defaultPriceForProduct(productId: number): Promise<number | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/purchase-orders/defaultPriceForProduct`,
      headers: {
        'X-Tenant': tenant,
      },
      params: { productId },
    };

    return await handleRequest<number>(config);
  }

  async function exportPurchaseOrders(req: PurchaseOrderSearchRequest): Promise<BlobPart> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/purchase-orders/export`,
      headers: {
        'X-Tenant': tenant,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      },
      data: req,
      responseType: 'blob',
    };
    return await handleRequest(config, true);
  }

  return {
    createPurchaseOrder,
    updatePurchaseOrder,
    searchPurchaseOrders,
    getPurchaseOrderById,
    approvePurchaseOrder,
    receivePurchaseOrder,
    cancelPurchaseOrder,
    listPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    defaultPriceForProduct,
    exportPurchaseOrders,
  };
};
